import { createContext, useContext, ReactNode, useState, useEffect, useCallback } from "react";
import { hasFullInlineTemplate, getBrandTemplate, getInlineTemplateName } from "@/utils/brandTemplate";
import { useSessionContext } from "@/contexts/SessionContext";

function hexToHSL(hex: string): string {
  hex = hex.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  let h = 0,
    s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export type BrandConfig = {
  id?: string;
  name: string;
  nameAr: string;
  logo: string;
  logoType: "emoji" | "image";
  tagline: string;
  taglineAr: string;
  currency: string;
  currencySymbol: string;
  primaryColor: string;
  secondaryColor: string;
  supportEmail: string;
  socialLinks: { platform: string; url: string }[];
  appStoreUrl?: string;
  googlePlayUrl?: string;
  template: "classic" | "elegant" | "modern" | "landing" | "heritage";
  productsAvailableInAllBranches: boolean;
  paymentMethod: string;
  autoAcceptOrder: boolean;
  paymentApprovalMode: string;
  allowCoupon: boolean;
  orderMinimumAllowed: number;
  hasSchedule: boolean;
  isDeliveryActive: boolean;
  isPickUpActive: boolean;
  isDineInActive: boolean;
  vatRate: number;
  taxInclusive: boolean;
  heroVideoUrl: string;
  whatsAppNumber?: string;
};

// Template name from index.html — available synchronously
const inlineTemplate = getInlineTemplateName();

const defaultBrand: BrandConfig = {
  name: "My Restaurant",
  nameAr: "",
  logo: "🍔",
  logoType: "emoji",
  tagline: "Fast food, delivered fresh.",
  taglineAr: "طعام سريع، يصل طازجاً.",
  currency: "USD",
  currencySymbol: "$",
  primaryColor: "#FF6B35",
  secondaryColor: "#2A9D8F",
  supportEmail: "support@quickbite.com",
  socialLinks: [
    { platform: "twitter", url: "#" },
    { platform: "facebook", url: "#" },
    { platform: "instagram", url: "#" },
  ],
  appStoreUrl: "#",
  googlePlayUrl: "#",
  template: inlineTemplate,
  productsAvailableInAllBranches: false,
  paymentMethod: "card",
  autoAcceptOrder: false,
  paymentApprovalMode: "auto",
  allowCoupon: true,
  orderMinimumAllowed: 0,
  hasSchedule: false,
  isDeliveryActive: true,
  isPickUpActive: true,
  isDineInActive: false,
  vatRate: 0,
  taxInclusive: false,
  heroVideoUrl: "",
  whatsAppNumber:"201029390907"
};

type BrandContextType = {
  brand: BrandConfig;
  loading: boolean;
  refetchBrand: () => Promise<void>;
  localizedName: (lang: string) => string;
  localizedTagline: (lang: string) => string;
};

/**
 * If serve-index injected a full template, map it to BrandConfig synchronously.
 * In dev (minimal inline), returns null so the API fetch is used instead.
 */
function mapFullInlineTemplateToBrand(): BrandConfig | null {
  if (!hasFullInlineTemplate()) return null;
  const tpl = getBrandTemplate();
  const b = tpl.brand;
  const f = tpl.features;
  return {
    id: b.id || undefined,
    name: b.name,
    nameAr: b.name_ar,
    logo: b.logo_type === "image" ? b.logo || "" : b.logo_emoji || "🍔",
    logoType: b.logo_type,
    tagline: b.tagline,
    taglineAr: b.tagline_ar,
    currency: b.currency_code,
    currencySymbol: b.currency_symbol,
    primaryColor: tpl.theme.primary_color,
    secondaryColor: tpl.theme.secondary_color,
    supportEmail: b.support_email || "",
    socialLinks: [
      { platform: "twitter", url: tpl.social?.twitter || "#" },
      { platform: "facebook", url: tpl.social?.facebook || "#" },
      { platform: "instagram", url: tpl.social?.instagram || "#" },
    ],
    template: b.template,
    productsAvailableInAllBranches: f.productsAvailableInAllBranches ?? false,
    paymentMethod: f.payment_method,
    autoAcceptOrder: f.auto_accept_order,
    paymentApprovalMode: f.payment_approval_mode,
    allowCoupon: f.allow_coupon,
    orderMinimumAllowed: f.order_minimum_allowed,
    hasSchedule: f.has_schedule,
    isDeliveryActive: f.is_delivery_active,
    isPickUpActive: f.is_pickup_active,
    isDineInActive: (f as any).is_dinein_active ?? false,
    vatRate: f.vat_rate,
    taxInclusive: f.tax_inclusive ?? false,
    heroVideoUrl: b.hero_video_url || "",
    whatsAppNumber: b.whatsAppNumber || undefined,
  };
}

function mapResponseToBrand(d: any): BrandConfig {
  return {
    ...defaultBrand,
    id: d.id,
    name: d.name || defaultBrand.name,
    nameAr: d.name_ar || "",
    logo: d.logo_type === "image" ? d.logo_url || "" : d.logo_emoji || "🍔",
    logoType: (d.logo_type || "emoji") as "emoji" | "image",
    tagline: d.tagline || defaultBrand.tagline,
    taglineAr: d.tagline_ar || defaultBrand.taglineAr,
    currency: d.currency_code || "USD",
    currencySymbol: d.currency_symbol || "$",
    primaryColor: d.primary_color || defaultBrand.primaryColor,
    secondaryColor: d.secondary_color || defaultBrand.secondaryColor,
    // Template always from inline (index.html), never from API
    template: inlineTemplate,
    productsAvailableInAllBranches: d.products_available_in_all_branches ?? false,
    paymentMethod: d.payment_method || "card",
    autoAcceptOrder: d.auto_accept_order ?? false,
    paymentApprovalMode: d.payment_approval_mode || "auto",
    allowCoupon: d.allow_coupon ?? true,
    orderMinimumAllowed: d.order_minimum_allowed ?? 0,
    hasSchedule: d.has_schedule ?? false,
    isDeliveryActive: d.is_delivery_active ?? true,
    isPickUpActive: d.is_pickup_active ?? true,
    isDineInActive: d.is_dinein_active ?? false,
    vatRate: d.vat_rate ?? 0,
    taxInclusive: d.tax_inclusive ?? false,
    heroVideoUrl: d.hero_video_url || "",
    whatsAppNumber: d.whatsAppNumber || undefined,
  };
}

function applyBrandCSSVars(primaryColor: string, secondaryColor: string) {
  if (primaryColor) {
    const hsl = hexToHSL(primaryColor);
    document.documentElement.style.setProperty("--primary", hsl);
    document.documentElement.style.setProperty("--ring", hsl);
    document.documentElement.style.setProperty("--sidebar-primary", hsl);
    document.documentElement.style.setProperty("--sidebar-ring", hsl);
  }
  if (secondaryColor) {
    const hsl = hexToHSL(secondaryColor);
    document.documentElement.style.setProperty("--secondary", hsl);
    document.documentElement.style.setProperty("--accent", hsl);
    document.documentElement.style.setProperty("--warning", hsl);
  }
}

// If serve-index provided a full template, apply CSS vars immediately
const fullInlineBrand = mapFullInlineTemplateToBrand();
if (fullInlineBrand) {
  applyBrandCSSVars(
    fullInlineBrand.primaryColor,
    fullInlineBrand.secondaryColor,
  );
}

const initialBrand = fullInlineBrand || defaultBrand;

const BrandContext = createContext<BrandContextType>({
  brand: initialBrand,
  loading: !fullInlineBrand, // loading if we need to fetch from API
  refetchBrand: async () => {},
  localizedName: () => initialBrand.name,
  localizedTagline: () => initialBrand.tagline,
});

export function BrandProvider({
  restaurantId,
  children,
}: {
  restaurantId?: string;
  children: ReactNode;
}) {
  const [brand, setBrand] = useState<BrandConfig>(initialBrand);
  const [loading, setLoading] = useState(!fullInlineBrand);
  const { setRestaurantId } = useSessionContext();

  const fetchBrand = useCallback(async () => {
    try {
      setLoading(true);
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
      // const url = restaurantId
      //   ? `https://${projectId}.supabase.co/functions/v1/get-brand?id=${restaurantId}`
      //   : `https://${projectId}.supabase.co/functions/v1/get-brand`;

      const baseUrl = window.location.origin;
      const url = restaurantId
        ? `${baseUrl}/api/WebOrder/functions/v1/get-brand?id=${restaurantId}`
        : `${baseUrl}/api/WebOrder/functions/v1/get-brand`;
      
      // const baseUrl = "https://saasmw.alkhwarizmi.xyz/api/WebOrder";
      // const url = restaurantId
      //   ? `${baseUrl}/functions/v1/get-brand?id=${restaurantId}`
      //   : `${baseUrl}/functions/v1/get-brand`;
      const res = await fetch(url, {
        headers: { apikey: anonKey, Authorization: `Bearer ${anonKey}` },
      });
      if (!res.ok) throw new Error("Failed to fetch brand");
      const d = await res.json();
      const mapped = mapResponseToBrand(d);
      setBrand(mapped);
      applyBrandCSSVars(mapped.primaryColor, mapped.secondaryColor);
      
      // Save restaurant_id to session storage
      if (d.id) {
        setRestaurantId(d.id);
      }
    } catch (err) {
      console.error("Error fetching brand:", err);
    } finally {
      setLoading(false);
    }
  }, [restaurantId, setRestaurantId]);

  useEffect(() => {
    // If we have a full inline template, save restaurant_id from it and skip the API fetch
    if (fullInlineBrand) {
      if (fullInlineBrand.id) {
        setRestaurantId(fullInlineBrand.id);
      }
      return;
    }
    fetchBrand();
  }, [fetchBrand, setRestaurantId]);

  const localizedName = useCallback(
    (lang: string) => {
      return lang === "ar" && brand.nameAr ? brand.nameAr : brand.name;
    },
    [brand.name, brand.nameAr],
  );

  const localizedTagline = useCallback(
    (lang: string) => {
      return lang === "ar" && brand.taglineAr ? brand.taglineAr : brand.tagline;
    },
    [brand.tagline, brand.taglineAr],
  );

  return (
    <BrandContext.Provider
      value={{
        brand,
        loading,
        refetchBrand: fetchBrand,
        localizedName,
        localizedTagline,
      }}
    >
      {children}
    </BrandContext.Provider>
  );
}

export function useBrand() {
  return useContext(BrandContext);
}
